import { css } from 'aphrodite';
import { parseAsBoolean, parseAsIsoDate, parseAsString, parseAsStringEnum, useQueryState, useQueryStates } from 'nuqs';
import * as React from 'react';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { DealStageOptions, DemoStatusFilter } from '../../../extViewmodels';
import { AIConversationType } from '../../../extViewmodels/sdk/models/AI';
import { getDisplayName } from '../../../models/NameUtils';
import { useLeadDemosQuery } from '../../../queries/Lead/useLeadDemos';
import { useLeadDemosToConfirmQuery } from '../../../queries/Lead/useLeadDemosToConfirmQuery';
import { AiButton } from '../../../web/components-v2/AiButton';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import { Dialog } from '../../../web/components-v2/Dialog';
import {
	AppDrawerContent,
	AppDrawerToolbarButton,
	CoffeeDrawerProvider,
	CoffeeDrawerToolbar,
	SideDrawer,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { Databoard, DataboardBody } from '../../../web/components/Databoard';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../web/components/DataGrid';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { bs } from '../../../web/styles/styles';
import { DemoRecordingsModal, IDemoRecording } from '../../components/DemoRecordingsModal';
import { useSingleDemoAnalysis } from '../../components/DemoRecordingsModal/useSingleDemoAnalysis';
import { OutcomeInfo } from '../../components/leadView/components/OutcomeInfo';
import { RecordingIndicator } from '../../components/leadView/components/RecordingIndicator';
import { TextColumn } from '../../components/leadView/components/TextColumn';
import { DemoDateWithRecording } from '../../components/leadView/LeadViewRowElement/components/DemoDateWithRecording';
import { styleSheet } from '../../components/leadView/LeadViewRowElement/styles';
import { destructive, gold, green } from '../../styles/colors';
import { FieldKey } from '../../viewModels/form';
import { ILeadSearchView, IPhoneCallLogView } from '../../viewModels/leads/interfaces';
import { LeadServedSource } from '../../viewModels/queue';
import { BulkActionsBar } from './Analysis/components/BulkActionsBar';
import { DemoAnalysisDrawerContent } from './Analysis/components/DemoAnalysisDrawerContent';
import { DemoAnalysisModal } from './Analysis/components/DemoAnalysisModal';
import { DEMO_ANALYSIS_DRAWER_VALUE } from './Analysis/constants';
import { useMultiDemoSelectionAnalysis } from './Analysis/hooks/useMultiDemoSelectionAnalysis';
import { Filters } from './Filters';
import { CoachingTemplateSelector } from '../Settings/CallAnalysis/components/CoachingTemplateSelector';
import { styleSheet as demosStyleSheet } from './styles';

// Get attempts display text
const getAttemptsText = (searchView: ILeadSearchView) => {
	const confirmationAttempts = searchView.nextMeeting?.confirmationAttempts || 0;
	if (confirmationAttempts === 0) return 'No Attempts';
	return `${confirmationAttempts} Attempt${confirmationAttempts > 1 ? 's' : ''}`;
};

// Get attempts color based on confirmation status and attempt count
const getAttemptsColor = (searchView: ILeadSearchView) => {
	// Determine if demo is confirmed - check if the outcome name is "Demo Confirmed"
	const isDemoConfirmed = searchView.lastInteraction?.outcome?.name === 'Demo Confirmed';
	if (isDemoConfirmed) {
		return green; // Green for confirmed demos
	}
	const confirmationAttempts = searchView.nextMeeting?.confirmationAttempts || 0;
	// For unconfirmed demos, use traffic light system
	if (confirmationAttempts <= 1) {
		return destructive; // Red for 0-1 attempts
	} else if (confirmationAttempts === 2) {
		return gold; // Yellow for 2 attempts
	} else {
		return green; // Green for 3+ attempts
	}
};

// Deal stages that should NOT show the recording link (lowercase for case-insensitive comparison)
const EXCLUDED_RECORDING_STAGES = ['appointment no show', 'appointment rescheduled', 'appointment scheduled'] as const;

// Check if a deal stage should exclude the recording link
const isExcludedRecordingStage = (stage?: string) => {
	if (!stage) return false;
	return EXCLUDED_RECORDING_STAGES.some(excluded => stage.toLowerCase() === excluded.toLowerCase());
};

export const DemosContainer: React.FC = () => {
	const id = React.useId();
	const [userId] = useQueryState('userId', parseAsString);
	const [dateRange] = useQueryStates({
		from: parseAsIsoDate,
		to: parseAsIsoDate,
	});
	const [demoStatus] = useQueryState(
		'demoStatus',
		parseAsStringEnum<DemoStatusFilter>(Object.values(DemoStatusFilter)).withDefault(DemoStatusFilter.DemosToConfirm)
	);
	const [outcomeName] = useQueryState('outcomeName', parseAsStringEnum<FieldKey>(Object.values(FieldKey)));
	const [managerUserId] = useQueryState('managerUserId', parseAsString);
	const [demoPerformerUserId] = useQueryState('demoPerformerUserId', parseAsString);
	const [companyId] = useQueryState('companyId', parseAsString);

	// Past Demos tab filter state
	const [dealStatus] = useQueryState('dealStatus', parseAsString);
	const [leadSource] = useQueryState('leadSource', parseAsString);
	const [addOn] = useQueryState('addOn', parseAsString);
	const [inboundChannel] = useQueryState('inboundChannel', parseAsString);
	const [lostReasonCategories] = useQueryState('lostReasonCategories', parseAsString);
	const [industry] = useQueryState('industry', parseAsString);
	const [tag] = useQueryState('tag', parseAsString);
	const [isColdDemo] = useQueryState('isColdDemo', parseAsBoolean);
	const [overbook] = useQueryState('overbook', parseAsBoolean);
	const [followUpDateRange] = useQueryStates({
		followUpDateStart: parseAsIsoDate,
		followUpDateEnd: parseAsIsoDate,
	});
	const [targetCloseDateRange] = useQueryStates({
		targetCloseDateStart: parseAsIsoDate,
		targetCloseDateEnd: parseAsIsoDate,
	});
	const [closedDateRange] = useQueryStates({
		closedDateStart: parseAsIsoDate,
		closedDateEnd: parseAsIsoDate,
	});

	// State for demo selection
	const [selectedDemos, setSelectedDemos] = useState<Set<string>>(new Set());
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

	// Drawer state for demo analysis
	const [selectedDrawerValue, setSelectedDrawerValue] = React.useState<string | null>(null);

	let dealStage: string[] = [];
	switch (demoStatus) {
		case DemoStatusFilter.DemosToReschedule:
			dealStage = [DealStageOptions.AppointmentNoShow];
			break;
		case DemoStatusFilter.UpcomingDemos:
			dealStage = [DealStageOptions.AppointmentScheduled, DealStageOptions.AppointmentRescheduled];
			break;
		case DemoStatusFilter.PastDemos:
			// Add selected deal stage from dropdown filter
			if (dealStatus) {
				dealStage = [dealStatus];
			}
			break;
		default:
			break;
	}
	const dataSource =
		dealStage.length === 0 && demoStatus !== DemoStatusFilter.PastDemos && !companyId ? 'demosToConfirm' : 'demos';
	const leadDemosToConfirmQuery = useLeadDemosToConfirmQuery({
		enabled: dataSource === 'demosToConfirm',
		userId,
		managerUserId,
		companyId,
		outcomeName: outcomeName !== FieldKey.Unknown ? outcomeName : undefined,
		demoPerformerUserId,
		start: dateRange.from,
		end: dateRange.to,
		meta: {
			toastErrorMessage: 'An unexpected error occurred while loading demos.',
			errorLogging: {
				category: 'DemosContainer',
				action: 'useLeadDemosToConfirmQuery',
			},
		},
	});
	const leadDemosQuery = useLeadDemosQuery({
		enabled: dataSource === 'demos',
		userId,
		managerUserId,
		companyId,
		outcomeName: outcomeName !== FieldKey.Unknown ? outcomeName : undefined,
		demoPerformerUserId,
		start: dateRange.from,
		end: dateRange.to,
		dealStage,
		// Past Demos tab filters
		leadSource: leadSource ?? undefined,
		addOn: addOn ?? undefined,
		inboundChannel: inboundChannel ?? undefined,
		lostReasonCategories: lostReasonCategories ?? undefined,
		industry: industry ?? undefined,
		tag: tag ?? undefined,
		isColdDemo: isColdDemo ?? undefined,
		overbook: overbook ?? undefined,
		followUpDateStart: followUpDateRange.followUpDateStart ?? undefined,
		followUpDateEnd: followUpDateRange.followUpDateEnd ?? undefined,
		targetCloseDateStart: targetCloseDateRange.targetCloseDateStart ?? undefined,
		targetCloseDateEnd: targetCloseDateRange.targetCloseDateEnd ?? undefined,
		closedDateStart: closedDateRange.closedDateStart ?? undefined,
		closedDateEnd: closedDateRange.closedDateEnd ?? undefined,
		meta: {
			toastErrorMessage: 'An unexpected error occurred while loading demos.',
			errorLogging: {
				category: 'DemosContainer',
				action: 'useLeadDemosQuery',
			},
		},
	});
	const activeQuery = dataSource === 'demosToConfirm' ? leadDemosToConfirmQuery : leadDemosQuery;
	const isLoading = activeQuery.isLoading;
	const isSuccess = activeQuery.isSuccess;
	const isEmpty = activeQuery.isSuccess && activeQuery.data.pages[0].values.length === 0;

	const [recordingsModal, setRecordingsModal] = useState<{
		demoId?: string;
		demoDate?: string;
		companyName?: string;
	}>({});

	// Single demo analysis hook for analyzing individual recordings from the modal
	const {
		isAnalysisModalOpen: isSingleAnalysisModalOpen,
		startAnalysis: startSingleAnalysis,
		closeAnalysis: closeSingleAnalysis,
		isLoading: isSingleAnalysisLoading,
		isCreatingConversation: isSingleAnalysisCreatingConversation,
		sendFollowUp: sendSingleAnalysisFollowUp,
		conversationId: singleAnalysisConversationId,
		aiPromptMutation: singleAnalysisAiPromptMutation,
		analysisPhase: singleAnalysisPhase,
	} = useSingleDemoAnalysis();

	const [analyzingRecordingId, setAnalyzingRecordingId] = useState<string | null>(null);

	const handleRecordingClick = (demoId: string, demoDate: string, _closerOwnerId: string, companyName?: string) => {
		setRecordingsModal({
			demoId,
			demoDate,
			companyName,
		});
	};

	const handleCloseModal = () => {
		setRecordingsModal({ demoId: undefined, demoDate: undefined, companyName: undefined });
	};

	const handleStartSingleAnalysis = (recording: IDemoRecording, templateId?: string) => {
		setAnalyzingRecordingId(recording.uuid);
		startSingleAnalysis(recording, templateId);
		// Close the recordings modal when starting analysis
		handleCloseModal();
	};

	const handleCloseSingleAnalysis = () => {
		setAnalyzingRecordingId(null);
		closeSingleAnalysis();
	};

	// Multi-demo selection analysis hook
	const {
		startMultiAnalysis,
		closeAnalysis: closeMultiAnalysis,
		isLoading: isMultiAnalysisLoading,
		isLoadingTranscripts: isMultiAnalysisLoadingTranscripts,
		isCreatingConversation: isMultiAnalysisCreatingConversation,
		sendFollowUp: sendMultiAnalysisFollowUp,
		conversationId: multiAnalysisConversationId,
		aiPromptMutation: multiAnalysisAiPromptMutation,
		// New hierarchical analysis state
		analysisPhase: multiAnalysisPhase,
		currentDemoIndex: multiCurrentDemoIndex,
		totalDemos: multiTotalDemos,
		individualAnalyses: multiIndividualAnalyses,
	} = useMultiDemoSelectionAnalysis({
		selectedLeadSearchViews: activeQuery.data
			? activeQuery.data.pages.flatMap(page => page.values).filter(item => selectedDemos.has(item.company?.id || ''))
			: [],
		source: 'demos_page_selection',
		agentTemplateId: selectedTemplateId || undefined,
	});

	const isPastDemosTab = demoStatus === DemoStatusFilter.PastDemos;

	// Computed drawer value - only open if we have analysis context
	const hasAnalysisResults = multiAnalysisPhase !== 'idle' || multiIndividualAnalyses.length > 0;
	const drawerValue = hasAnalysisResults ? selectedDrawerValue : null;

	// Handle demo selection
	const handleDemoSelect = (demoId: string, checked: boolean) => {
		const newSelected = new Set(selectedDemos);
		if (checked) {
			newSelected.add(demoId);
		} else {
			newSelected.delete(demoId);
		}
		setSelectedDemos(newSelected);
	};

	// Check if a demo should show the recording link (has recording capability)
	const hasRecordingLink = (item: ILeadSearchView) => {
		const itemCompanyId = item.company?.id;
		const closerOwnerId = item.lastDeal?.creator?.id;
		const itemDealStage = item.lastDeal?.stage;
		return itemCompanyId && closerOwnerId && !isExcludedRecordingStage(itemDealStage);
	};

	// Handle select all demos with recordings
	const handleSelectAllWithRecordings = (checked: boolean) => {
		if (!isSuccess || !activeQuery.data) return;
		const data = activeQuery.data.pages.flatMap(page => page.values);
		const newSelected = new Set<string>();
		if (checked) {
			for (const item of data) {
				if (hasRecordingLink(item)) {
					newSelected.add(item.company!.id);
				}
			}
		}
		setSelectedDemos(newSelected);
	};

	// Compute select all checkbox state (memoized to avoid recalculating on every render)
	const selectAllState = React.useMemo(() => {
		if (!isSuccess || !activeQuery.data) return { checked: false, indeterminate: false };
		const data = activeQuery.data.pages.flatMap(page => page.values);
		const demosWithRecordingLink = data.filter(hasRecordingLink);
		if (demosWithRecordingLink.length === 0) return { checked: false, indeterminate: false };
		const selectedCount = demosWithRecordingLink.filter(item => selectedDemos.has(item.company!.id)).length;
		const allSelected = selectedCount === demosWithRecordingLink.length;
		const someSelected = selectedCount > 0 && !allSelected;
		return { checked: allSelected, indeterminate: someSelected };
	}, [isSuccess, activeQuery.data, selectedDemos]);

	// Handle analyze selected demos - auto-open drawer
	const handleAnalyzeSelected = () => {
		if (selectedDemos.size === 0) return;
		startMultiAnalysis();
		setSelectedDrawerValue(DEMO_ANALYSIS_DRAWER_VALUE);
	};

	// Handle close multi-analysis
	const handleCloseMultiAnalysis = () => {
		closeMultiAnalysis();
		setSelectedDrawerValue(null);
		setSelectedDemos(new Set());
	};

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	const noop = React.useCallback(() => {}, []);

	if (isLoading) {
		return (
			<CoffeeDrawerProvider value={null} onValueChange={noop}>
				<div className={css(demosStyleSheet.pageRoot)}>
					<div className={css(demosStyleSheet.pageContent)}>
						<DemosLayout>
							<LoadingSpinner type='large' />
						</DemosLayout>
					</div>
				</div>
			</CoffeeDrawerProvider>
		);
	}
	if (isEmpty) {
		return (
			<CoffeeDrawerProvider value={null} onValueChange={noop}>
				<div className={css(demosStyleSheet.pageRoot)}>
					<div className={css(demosStyleSheet.pageContent)}>
						<DemosLayout>
							<div className={css(bs.textCenter, bs.p2)}>No data to show</div>
						</DemosLayout>
					</div>
					{isPastDemosTab && (
						<SideDrawer styles={[demosStyleSheet.drawer]}>
							<AppDrawerContent value={DEMO_ANALYSIS_DRAWER_VALUE} styles={[demosStyleSheet.drawerContent]}>
								<DemoAnalysisDrawerContent
									onClose={noop}
									isLoading={false}
									isCreatingConversation={false}
									onSendFollowUp={noop}
									conversationId=''
									aiPromptMutation={{ isLoading: false, isStreaming: false } as any}
								/>
							</AppDrawerContent>
							<CoffeeDrawerToolbar>
								<AppDrawerToolbarButton value={DEMO_ANALYSIS_DRAWER_VALUE} tooltip='Demo analysis' disabled={true}>
									<SparklesIcon />
								</AppDrawerToolbarButton>
							</CoffeeDrawerToolbar>
						</SideDrawer>
					)}
				</div>
			</CoffeeDrawerProvider>
		);
	}

	if (isSuccess) {
		const data = activeQuery.data.pages.flatMap(page => page.values);

		return (
			<CoffeeDrawerProvider value={drawerValue} onValueChange={setSelectedDrawerValue}>
				<div className={css(demosStyleSheet.pageRoot)}>
					<div className={css(demosStyleSheet.pageContent)}>
						<DemosLayout>
							{isPastDemosTab && selectedDemos.size > 0 && (
								<BulkActionsBar selectedDemoCount={selectedDemos.size}>
									<CoachingTemplateSelector
										value={selectedTemplateId}
										onValueChange={setSelectedTemplateId}
										coachingTypes={[AIConversationType.SalesDemoCallCoaching]}
									/>
									<AiButton
										title='Analyze Selected'
										onClick={handleAnalyzeSelected}
										disabled={isMultiAnalysisLoadingTranscripts}
										isLoading={isMultiAnalysisLoadingTranscripts}
										size='sm'
									/>
								</BulkActionsBar>
							)}
							<Databoard>
								<DataboardBody styles={[demosStyleSheet.databoardBodyOverride]}>
									<DataGrid>
										<DataGridColumns>
											{isPastDemosTab && (
												<DataGridColumn>
													<div style={{ marginLeft: -10 }}>
														<Checkbox
															checked={selectAllState.indeterminate ? 'indeterminate' : selectAllState.checked}
															onCheckedChange={checked => handleSelectAllWithRecordings(checked === true)}
															aria-label='Select all demos with recordings'
														/>
													</div>
												</DataGridColumn>
											)}
											<DataGridColumn>Demo perform date</DataGridColumn>
											<DataGridColumn>Company info</DataGridColumn>
											<DataGridColumn>SDR</DataGridColumn>
											<DataGridColumn>Last call</DataGridColumn>
											<DataGridColumn>Recording</DataGridColumn>
										</DataGridColumns>
										<DataGridRows>
											{data.map((item, index) => {
												const demoId = item.company?.id || '';
												const isSelected = selectedDemos.has(demoId);
												return (
													<DataGridRow key={`${id}-lead-search-row-${index}`}>
														{isPastDemosTab && (
															<DataGridRowItem>
																<Checkbox
																	checked={isSelected}
																	onCheckedChange={checked => handleDemoSelect(demoId, checked as boolean)}
																	aria-label={`Select demo for ${item.company?.companyName || 'Unknown'}`}
																/>
															</DataGridRowItem>
														)}
														<DataGridRowItem>
															<DemoDateWithRecording
																dateValue={item.nextMeeting?.startDate ?? item.lastDeal?.demoPerformDate}
																demoId={item.company?.id}
																closerOwnerId={item.lastDeal?.creator?.id}
																dealStage={item.lastDeal?.stage}
																companyName={item.company?.companyName}
																onRecordingClick={handleRecordingClick}
															/>
														</DataGridRowItem>
														<DataGridRowItem>
															{item.company?.id ? (
																<Button asChild variant='link' styles={[styleSheet.companyLink]}>
																	<NavLink
																		to={`/queue?from=${LeadServedSource.AppointmentConfirmation}&companyId=${item.company?.id}`}
																	>
																		<TextColumn
																			text={item.company?.companyName || 'Unknown'}
																			subtext={item.company?.phoneNumbers[0]?.metadata?.standard || 'Unknown'}
																		/>
																	</NavLink>
																</Button>
															) : (
																<TextColumn
																	text={item.company?.companyName || 'Unknown'}
																	subtext={item.company?.phoneNumbers[0]?.metadata?.standard || 'Unknown'}
																/>
															)}
														</DataGridRowItem>
														<DataGridRowItem>
															<TextColumn
																text={item.lastDeal?.creator ? getDisplayName(item.lastDeal.creator) : 'Unknown'}
																isSmall={true}
															/>
														</DataGridRowItem>
														<DataGridRowItem>
															<div>
																<OutcomeInfo
																	name={item.lastInteraction?.outcome?.name}
																	color={item.lastInteraction?.outcome?.hexColor}
																	date={item.lastInteraction?.date}
																	meetingParticipants={item.nextMeeting?.participants}
																/>
																<div className={css(styleSheet.lastInteraction)}>
																	<div className={css(styleSheet.dot)} style={{ background: getAttemptsColor(item) }} />
																	<span>{getAttemptsText(item)}</span>
																</div>
															</div>
														</DataGridRowItem>
														<DataGridRowItem>
															<RecordingIndicator
																creationDate={(item as IPhoneCallLogView).phoneCall?.creationDate}
																creatorName={getDisplayName((item as IPhoneCallLogView).phoneCall?.creator)}
																phoneNumber={(item as IPhoneCallLogView).phoneCall?.phoneNumber?.standard}
																id={(item as IPhoneCallLogView).phoneCall?.id}
																publicUrl={(item as IPhoneCallLogView).phoneCall?.recording?.publicUrl}
															/>
														</DataGridRowItem>
													</DataGridRow>
												);
											})}
											{activeQuery.hasNextPage && !activeQuery.isFetchingNextPage ? (
												<Waypoint
													key='load-more-trigger'
													bottomOffset='-200px'
													onEnter={() => activeQuery.fetchNextPage()}
												/>
											) : null}
										</DataGridRows>
									</DataGrid>
									{activeQuery.isFetchingNextPage ? (
										<div className={css(bs.flex, bs.justifyCenter, bs.p2)}>
											<LoadingSpinner type='small' />
										</div>
									) : null}
								</DataboardBody>
								<Dialog
									open={Boolean(recordingsModal.demoId)}
									onOpenChange={open => {
										if (!open) {
											handleCloseModal();
										}
									}}
								>
									<DemoRecordingsModal
										demoId={recordingsModal.demoId}
										demoDate={recordingsModal.demoDate}
										companyName={recordingsModal.companyName}
										onStartAnalysis={handleStartSingleAnalysis}
										analyzingRecordingId={analyzingRecordingId}
										isAnalyzeLoading={isSingleAnalysisLoading}
									/>
								</Dialog>
								{isSingleAnalysisModalOpen && (
									<DemoAnalysisModal
										isOpen={isSingleAnalysisModalOpen}
										onClose={handleCloseSingleAnalysis}
										isLoading={isSingleAnalysisLoading}
										isCreatingConversation={isSingleAnalysisCreatingConversation}
										onSendFollowUp={sendSingleAnalysisFollowUp}
										conversationId={singleAnalysisConversationId}
										aiPromptMutation={singleAnalysisAiPromptMutation}
										analysisPhase={singleAnalysisPhase}
									/>
								)}
							</Databoard>
						</DemosLayout>
					</div>
					{isPastDemosTab && (
						<SideDrawer styles={[demosStyleSheet.drawer]}>
							<AppDrawerContent value={DEMO_ANALYSIS_DRAWER_VALUE} styles={[demosStyleSheet.drawerContent]}>
								<DemoAnalysisDrawerContent
									onClose={handleCloseMultiAnalysis}
									isLoading={isMultiAnalysisLoading}
									isCreatingConversation={isMultiAnalysisCreatingConversation}
									onSendFollowUp={sendMultiAnalysisFollowUp}
									conversationId={multiAnalysisConversationId}
									aiPromptMutation={multiAnalysisAiPromptMutation}
									analysisPhase={multiAnalysisPhase}
									currentDemoIndex={multiCurrentDemoIndex}
									totalDemos={multiTotalDemos}
									individualAnalyses={multiIndividualAnalyses}
								/>
							</AppDrawerContent>
							<CoffeeDrawerToolbar>
								<AppDrawerToolbarButton
									value={DEMO_ANALYSIS_DRAWER_VALUE}
									tooltip='Demo analysis'
									disabled={!hasAnalysisResults}
								>
									<SparklesIcon />
								</AppDrawerToolbarButton>
							</CoffeeDrawerToolbar>
						</SideDrawer>
					)}
				</div>
			</CoffeeDrawerProvider>
		);
	}
};

function DemosLayout({ children }: { children: React.ReactNode }) {
	return (
		<div className={css(demosStyleSheet.demosLayoutContainer)}>
			<div className={css(demosStyleSheet.demosLayoutContainerInner)}>
				<div className={css(demosStyleSheet.demosLayoutContent)}>
					<div className={css(demosStyleSheet.demosLayoutHeader)}>
						<h2 className={css(demosStyleSheet.demosLayoutTitle)}>Demos</h2>
					</div>
					<Filters />
					{children}
				</div>
			</div>
		</div>
	);
}
